import { useSnapshot } from 'valtio'

import type { Column } from '~shared/rundown/column'

import type { CellKey } from '../cell/cell.schema'
import { getCell } from '../data/cells.store'

import { CellSaveEntry, ColumnSaveEntry, saveKeys } from './grid.save'
import { isSaveEntryVisible } from './grid.save.helpers'
import { gridState } from './grid.store'

function useCellSaveEntry(cellKey: CellKey): CellSaveEntry | undefined {
  const snap = useSnapshot(gridState.saveRegistry)
  return snap[saveKeys.cell(cellKey)] as CellSaveEntry | undefined // TODO: rewrite to avoid casting
}

export function useColumnSaveEntry(
  columnId: string,
): ColumnSaveEntry | undefined {
  const snap = useSnapshot(gridState.saveRegistry)
  return snap[saveKeys.column(columnId)] as ColumnSaveEntry | undefined
}

/**
 * @param projectionValue Content from the listener store
 * @param projectionUpdatedAt `updatedAt` from the listener store
 */
export function useCellTextSaveState(
  cellKey: CellKey,
  projectionValue: string,
  projectionUpdatedAt: Date | undefined,
) {
  const entry = useCellSaveEntry(cellKey)
  const isVisible = isSaveEntryVisible(entry, projectionUpdatedAt)
  return {
    /** optimistic overlay: unsynced content wins over projection */
    displayValue:
      isVisible && entry?.payload.kind === 'cellText'
        ? entry.payload.content.text
        : projectionValue,
    isSaving: entry?.status === 'pending',
    error: entry?.status === 'error' ? entry.error : undefined,
  }
}

/**
 * @param projectionValue Selection from the listener store
 * @param projectionUpdatedAt `updatedAt` from the listener store
 */
export function useCellSelectSaveState(
  cellKey: CellKey,
  projectionValue: string,
  projectionUpdatedAt: Date | undefined,
) {
  const entry = useCellSaveEntry(cellKey)
  const isVisible = isSaveEntryVisible(entry, projectionUpdatedAt)
  return {
    /** optimistic overlay: unsynced content wins over projection */
    displayValue:
      isVisible && entry?.payload.kind === 'cellSelect'
        ? entry.payload.content.selected
        : projectionValue,
    isSaving: entry?.status === 'pending',
    error: entry?.status === 'error' ? entry.error : undefined,
  }
}

export function useColumnSaveState(
  columnId: Column['id'],
  projectionValue: readonly string[],
) {
  const entry = useColumnSaveEntry(columnId)
  return {
    /** optimistic overlay: unpersisted content wins over projection */
    displayValue:
      entry?.payload.kind === 'columnOptions'
        ? entry.payload.content.options
        : projectionValue,
    isSaving: entry?.status === 'pending',
    error: entry?.status === 'error' ? entry.error : undefined,
  }
}

export function useSaveIndicator(cellKey: CellKey, columnId?: Column['id']) {
  const snap = useSnapshot(gridState.saveRegistry)

  // Conditionally look up either cell or column entry. Note: case when both is not handled
  // A confirmed entry is already saved, so it has nothing to indicate
  const cellEntry = snap[saveKeys.cell(cellKey)]
  const entry =
    (cellEntry?.status === 'confirmed' ? undefined : cellEntry) ??
    (columnId ? snap[saveKeys.column(columnId)] : undefined)

  if (!entry) return undefined
  return entry
}

export function getCellTextOptimistic(
  cellKey: CellKey,
  fallback: string,
): string {
  const entry = gridState.saveRegistry[saveKeys.cell(cellKey)]
  const isVisible = isSaveEntryVisible(entry, getCell(cellKey)?.updatedAt)
  return isVisible && entry?.payload.kind === 'cellText'
    ? entry.payload.content.text
    : fallback
}

export function getColumnOptionsOptimistic(
  columnId: string,
  fallback: readonly string[],
): string[] {
  const entry = gridState.saveRegistry[saveKeys.column(columnId)]
  return entry?.payload.kind === 'columnOptions'
    ? [...entry.payload.content.options] // detach from the valtio proxy
    : [...fallback]
}
